import moment from 'moment'

import {OrderDto, OrderStatus} from '../../types'

// UE / CC are the "unconfirmed" variation codes whose booked delivery time is actually rescheduled
// to `variationCodeAdjustedTime` once the order is confirmed (AU RMC). Every other variation code is
// advisory and leaves the booked shipping date unchanged, so it must NOT drive the confirm flow here.
const UNCONFIRMED_ORDER_STATUSES: OrderStatus[] = [
  OrderStatus.Pending,
  OrderStatus.Reserved,
  OrderStatus.PaymentPending
]

// NOTE: mirrored by `isUeCcVariationCode` in
// packages/hub/src/Pages/Order/variationCode.utils.ts. Keep the UE/CC set in sync across both copies.
const isUeCcVariationCode = (variationCode?: string): boolean =>
  variationCode === 'UE' || variationCode === 'CC'

// `variationCodeOffset` is an ISO 8601 duration string (e.g. 'PT1H'); the zero-duration sentinel can
// appear as 'PT0S' / 'P0D' and still parses as a truthy, non-empty string, so we must check its
// actual magnitude rather than truthiness.
// NOTE: mirrored by `hasNonZeroVariationOffset` in
// packages/hub/src/Pages/Order/variationCode.utils.ts. Keep the zero-offset semantics in sync.
const hasNonZeroOffset = (variationCodeOffset?: string): boolean => {
  if (!variationCodeOffset) {
    return false
  }
  const duration = moment.duration(variationCodeOffset)
  return duration.isValid() && duration.asMilliseconds() !== 0
}

/**
 * Returns the rescheduled delivery time (`variationCodeAdjustedTime`) for an unconfirmed UE/CC order
 * with a non-zero offset, or `undefined` when the adjusted time must not be used as the confirm-flow
 * reference (confirmed orders, other/advisory codes, zero offset, or a missing/invalid adjusted time).
 */
export const getUeCcVariationCodeAdjustedTime = (order: OrderDto): string | undefined => {
  const {orderStatus, variationCode, variationCodeOffset, variationCodeAdjustedTime} = order

  if (
    !UNCONFIRMED_ORDER_STATUSES.includes(orderStatus) ||
    !isUeCcVariationCode(variationCode) ||
    !hasNonZeroOffset(variationCodeOffset) ||
    !variationCodeAdjustedTime
  ) {
    return undefined
  }

  return moment(variationCodeAdjustedTime).isValid() ? variationCodeAdjustedTime : undefined
}
